import { useEffect, useState, useCallback, Fragment } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { RowsSkeleton, Empty } from "../components/UI";
import { Trophy, ArrowUp, ArrowDown, Minus, Crown, ChevronRight, ChevronDown, Globe2, Flame, Info, Medal } from "lucide-react";

const WRAP = "mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12";
const STATUS_STYLE = {
  ACTIVE: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  CLOSED: "bg-[#E5B842]/15 text-[#E5B842] border-[#E5B842]/40",
  UPCOMING: "bg-slate-500/15 text-slate-300 border-slate-500/40",
};
const STATUS_LABEL = { ACTIVE: "Active", CLOSED: "Closed · Final", UPCOMING: "Upcoming" };

function Move({ change }) {
  if (change > 0) return <span className="inline-flex items-center gap-0.5 text-emerald-400 font-mono font-bold" aria-label={`Up ${change}`}><ArrowUp className="w-3.5 h-3.5" />{change}</span>;
  if (change < 0) return <span className="inline-flex items-center gap-0.5 text-red-400 font-mono font-bold" aria-label={`Down ${Math.abs(change)}`}><ArrowDown className="w-3.5 h-3.5" />{Math.abs(change)}</span>;
  return <span className="inline-flex items-center gap-1 text-slate-600 font-mono" aria-label="No change"><Minus className="w-3.5 h-3.5" /><span className="sr-only">No change</span></span>;
}

function tierLabel(code) {
  if (!code) return "—";
  return code.replace(/_/g, " ").replace("WSTF ", "WSTF ");
}

export default function SeasonRacePage() {
  const [seasons, setSeasons] = useState([]);
  const [season, setSeason] = useState(null);
  const [rows, setRows] = useState(null);
  const [country, setCountry] = useState("");
  const [scope, setScope] = useState("all"); // all | scored
  const [expanded, setExpanded] = useState(null);
  const [contrib, setContrib] = useState({}); // player_id -> {loading, results}

  const loadSeason = useCallback((s) => {
    setSeason(s); setRows(null); setExpanded(null); setContrib({});
    api.get(`/season/${s.id}/leaderboard`).then(({ data }) => setRows(data.leaderboard || []));
  }, []);

  useEffect(() => {
    api.get("/season/list").then(({ data }) => {
      const all = data.seasons || [];
      setSeasons(all);
      const active = all.find((s) => s.race_status === "ACTIVE");
      const def = active || all.find((s) => s.race_status === "CLOSED") || all[0];
      if (def) loadSeason(def); else setRows([]);
    });
  }, [loadSeason]);

  const toggleRow = (r) => {
    const pid = r.player_id;
    if (expanded === pid) { setExpanded(null); return; }
    setExpanded(pid);
    if (!contrib[pid]) {
      setContrib((c) => ({ ...c, [pid]: { loading: true, results: [] } }));
      api.get(`/season/${season.id}/player/${r.wsbf_player_id}/results`)
        .then(({ data }) => setContrib((c) => ({ ...c, [pid]: { loading: false, results: data.results || [] } })))
        .catch(() => setContrib((c) => ({ ...c, [pid]: { loading: false, results: [] } })));
    }
  };

  if (!rows) return <RowsSkeleton rows={8} />;

  const countries = Array.from(new Set(rows.map((r) => r.country_iso3).filter(Boolean))).sort();
  let shown = country ? rows.filter((r) => r.country_iso3 === country) : rows;
  if (scope === "scored") shown = shown.filter((r) => r.season_total > 0);
  const scoredCount = rows.filter((r) => r.season_total > 0).length;
  const status = season?.race_status;
  const top3 = shown.slice(0, 3);

  return (
    <div className="animate-fade-up pb-24 overflow-x-hidden">
      {/* ---- COMPACT CINEMATIC HERO ---- */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.14] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 120% at 20% -10%, rgba(212,175,55,0.20) 0%, transparent 55%)" }} />
        <div className={`${WRAP} relative py-10 sm:py-12`}>
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#E5B842] font-mono font-bold mb-3 flex items-center gap-2">
            <Trophy className="w-3.5 h-3.5" /> WSTF Season · Points Race
          </div>
          <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-slate-50 leading-none wsbf-slant-block">Season Race</h1>
            {season && (
              <span data-testid={status === "CLOSED" ? "season-closed-badge" : "season-status-badge"}
                className={`mb-1 text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-md border ${STATUS_STYLE[status] || STATUS_STYLE.UPCOMING}`}>
                {season.name} · {STATUS_LABEL[status] || status}
              </span>
            )}
          </div>
          <p data-testid="season-race-header" className="mt-4 max-w-2xl text-sm text-slate-400 flex items-start gap-2">
            <Info className="w-4 h-4 text-[#E5B842] shrink-0 mt-0.5" />
            <span>The Season Race is a <b className="text-slate-200 font-semibold">seasonal points race</b> — distinct from the ArenaBook <Link to="/rankings" className="text-[#E5B842] hover:underline">World Ranking</Link> (skill rating). Players earn WSTF tier points by finishing stage; only their best 8 events count, plus 25% carried from last season.</span>
          </p>
        </div>
      </section>

      <div className={`${WRAP} pt-8`}>
        {/* ---- FILTERS ---- */}
        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="season-filters">
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Season</label>
            <select data-testid="season-selector" value={season ? season.id : ""}
              onChange={(e) => { const s = seasons.find((x) => x.id === e.target.value); if (s) loadSeason(s); }}
              className="bg-[#0a0a0a] border border-[#2b2512] rounded-lg px-3 py-1.5 text-sm text-slate-200 hover:border-[#E5B842]/40" style={{ transition: "border-color 0.15s ease" }}>
              {seasons.map((s) => (
                <option key={s.id} value={s.id}>{s.name} {s.race_status === "ACTIVE" ? "· Active" : s.race_status === "CLOSED" ? "· Final" : s.race_status === "UPCOMING" ? "· Upcoming" : ""}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Country</label>
            <select data-testid="season-country-filter" value={country} onChange={(e) => setCountry(e.target.value)}
              className="bg-[#0a0a0a] border border-[#2b2512] rounded-lg px-3 py-1.5 text-sm text-slate-200 hover:border-[#E5B842]/40" style={{ transition: "border-color 0.15s ease" }}>
              <option value="">All countries</option>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="inline-flex gap-1 p-1 rounded-xl bg-[#0a0a0a] border border-[#2b2512]">
            {[["all", `All (${rows.length})`], ["scored", `Scored (${scoredCount})`]].map(([k, l]) => (
              <button key={k} data-testid={`season-scope-${k}`} onClick={() => setScope(k)}
                className={`px-3 py-1.5 rounded-lg text-xs font-heading font-bold uppercase ${scope === k ? "bg-[#E5B842] text-[#000000]" : "text-slate-300 hover:text-[#E5B842]"}`}
                style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>{l}</button>
            ))}
          </div>
          <span className="text-xs text-slate-500 font-mono ml-auto">{shown.length} players shown</span>
        </div>

        {shown.length === 0 ? <Empty label="No players for this filter" /> : (
          <>
            {/* ---- TOP 3 PODIUM ---- */}
            {scope !== "scored" || top3.some((r) => r.season_total > 0) ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="season-top3">
                {top3.map((r, i) => (
                  <Link key={r.player_id} to={`/player/${r.wsbf_player_id}`}
                    data-testid={`season-podium-${i + 1}`}
                    className={`relative overflow-hidden rounded-2xl p-5 group ${i === 0 ? "foil-border bg-gradient-to-br from-[#2a2410] to-[#070707] sm:-mt-2" : "border border-[#2b2512] bg-[#0f0f0f]"}`}
                    style={{ transition: "transform 0.2s ease" }}>
                    <div className="absolute inset-0 mesh-grid opacity-[0.12] pointer-events-none" />
                    {i === 0 && <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 140% at 10% 0%, rgba(212,175,55,0.18) 0%, transparent 60%)" }} />}
                    <div className="relative flex items-center justify-between">
                      <span className={`font-heading text-4xl font-black ${i === 0 ? "text-[#E5B842]" : "text-slate-600"}`}>{r.position}</span>
                      {i === 0 ? <Crown className="w-7 h-7 text-[#E5B842]" /> : <Medal className={`w-6 h-6 ${i === 1 ? "text-slate-300" : "text-amber-700"}`} />}
                    </div>
                    <div className="relative mt-3 font-heading text-lg font-black uppercase text-slate-50 truncate group-hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>{r.display_name}</div>
                    <div className="relative text-[11px] font-mono text-slate-500 truncate">{r.country_iso3}{r.club ? ` · ${r.club.name}` : ""}</div>
                    <div className="relative mt-4 flex items-end justify-between">
                      <div>
                        <div className="font-heading text-3xl font-black text-[#E5B842] tabular-nums leading-none">{r.season_total}</div>
                        <div className="text-[9px] font-mono uppercase tracking-wider text-slate-600 mt-1">Total points</div>
                      </div>
                      <div className="text-right text-[10px] font-mono text-slate-500 space-y-0.5">
                        <div>Carry <b className="text-slate-300">{r.carry_over}</b></div>
                        <div>Best-8 <b className="text-slate-300">{r.earned_best8}</b></div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : null}

            {/* ---- LEADERBOARD (desktop table) ---- */}
            <div className="hidden md:block rounded-xl border border-[#2b2512] overflow-hidden">
              <table className="w-full text-sm" data-testid="season-leaderboard">
                <thead className="bg-[#070707] text-slate-500 text-[10px] uppercase tracking-wider font-mono">
                  <tr>
                    <th className="px-4 py-3 text-left w-12">#</th>
                    <th className="px-2 py-3 text-left">Player</th>
                    <th className="px-3 py-3 text-right text-[#E5B842]">Total</th>
                    <th className="px-3 py-3 text-right">Carry</th>
                    <th className="px-3 py-3 text-right">Best 8</th>
                    <th className="px-3 py-3 text-right">Titles</th>
                    <th className="px-3 py-3 text-right">Played</th>
                    <th className="px-3 py-3 text-center">Move</th>
                    <th className="px-3 py-3 w-8" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const open = expanded === r.player_id;
                    const c = contrib[r.player_id];
                    return (
                      <Fragment key={r.player_id}>
                        <tr data-testid={`season-row-${r.player_id}`}
                          onClick={() => toggleRow(r)}
                          className={`border-t border-[#2b2512]/60 cursor-pointer ${open ? "bg-[#161616]/60" : "hover:bg-[#161616]/40"}`} style={{ transition: "background-color 0.15s ease" }}>
                          <td className="px-4 py-3 font-heading font-black text-[#E5B842]">{r.position}</td>
                          <td className="px-2 py-3">
                            <Link to={`/player/${r.wsbf_player_id}`} onClick={(e) => e.stopPropagation()} className="font-semibold text-slate-100 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>{r.display_name}</Link>
                            <div className="text-[10px] text-slate-500 font-mono">{r.country_iso3}{r.club ? ` · ${r.club.name}` : ""}</div>
                          </td>
                          <td className="px-3 py-3 text-right font-heading font-black text-slate-50 tabular-nums">{r.season_total}</td>
                          <td className="px-3 py-3 text-right text-slate-400 tabular-nums">{r.carry_over}</td>
                          <td className="px-3 py-3 text-right text-slate-300 tabular-nums">{r.earned_best8}</td>
                          <td className="px-3 py-3 text-right text-slate-300 tabular-nums">{r.titles}</td>
                          <td className="px-3 py-3 text-right text-slate-400 tabular-nums">{r.tournaments_played}</td>
                          <td className="px-3 py-3 text-center"><Move change={r.position_change} /></td>
                          <td className="px-3 py-3 text-slate-600">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                        </tr>
                        {open && (
                          <tr data-testid={`season-contrib-${r.player_id}`} className="bg-[#0a0a0a]">
                            <td colSpan={9} className="px-6 py-4">
                              <Contributions c={c} total={r.season_total} carry={r.carry_over} best8={r.earned_best8} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ---- LEADERBOARD (mobile cards) ---- */}
            <div className="md:hidden space-y-2" data-testid="season-leaderboard-mobile">
              {shown.map((r) => {
                const open = expanded === r.player_id;
                const c = contrib[r.player_id];
                return (
                  <div key={r.player_id} className="rounded-xl border border-[#2b2512] bg-[#0f0f0f] overflow-hidden">
                    <div onClick={() => toggleRow(r)} className="flex items-center gap-3 p-3 cursor-pointer" data-testid={`season-mrow-${r.player_id}`}>
                      <span className="font-heading text-xl font-black text-[#E5B842] w-7 text-center">{r.position}</span>
                      <div className="min-w-0 flex-1">
                        <Link to={`/player/${r.wsbf_player_id}`} onClick={(e) => e.stopPropagation()} className="font-semibold text-slate-100 block truncate hover:text-[#E5B842]">{r.display_name}</Link>
                        <div className="text-[10px] text-slate-500 font-mono truncate">{r.country_iso3}{r.club ? ` · ${r.club.name}` : ""}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-heading text-xl font-black text-[#E5B842] tabular-nums leading-none">{r.season_total}</div>
                        <div className="text-[9px] font-mono text-slate-600 uppercase">total</div>
                      </div>
                      <Move change={r.position_change} />
                      {open ? <ChevronDown className="w-4 h-4 text-slate-600" /> : <ChevronRight className="w-4 h-4 text-slate-600" />}
                    </div>
                    {open && (
                      <div className="px-3 pb-4 border-t border-[#2b2512]/60">
                        <div className="grid grid-cols-3 gap-2 py-3 text-center font-mono text-[11px]">
                          <div><div className="text-slate-500">Carry</div><div className="text-slate-200 font-bold">{r.carry_over}</div></div>
                          <div><div className="text-slate-500">Best-8</div><div className="text-slate-200 font-bold">{r.earned_best8}</div></div>
                          <div><div className="text-slate-500">Titles</div><div className="text-slate-200 font-bold">{r.titles}</div></div>
                        </div>
                        <Contributions c={c} total={r.season_total} carry={r.carry_over} best8={r.earned_best8} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Contributions({ c, carry, best8 }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#E5B842] font-mono font-bold mb-3 flex items-center gap-1.5"><Flame className="w-3.5 h-3.5" /> Season Contributions</div>
      {!c || c.loading ? (
        <div className="text-xs font-mono text-slate-500 py-2">Loading contributions…</div>
      ) : c.results.length === 0 ? (
        <div className="text-xs text-slate-500 py-2">
          No scoring events recorded this season yet.
          {carry > 0 && <> This player's <b className="text-slate-300">{carry}</b> total is a 25% carry-over from last season (best-8 earned this season: {best8}).</>}
        </div>
      ) : (
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-xs min-w-[640px]">
            <thead className="text-slate-500 text-[9px] uppercase font-mono">
              <tr>
                <th className="text-left py-1.5 pr-3">Event</th>
                <th className="text-left py-1.5 pr-3">Tier</th>
                <th className="text-left py-1.5 pr-3">Result</th>
                <th className="text-right py-1.5 pr-3">Tier / Season Points</th>
                <th className="text-left py-1.5">Best-8</th>
              </tr>
            </thead>
            <tbody>
              {c.results.map((rr) => (
                <tr key={rr.id} className={`border-t border-[#2b2512]/50 ${rr.counts_in_best8 ? "" : "opacity-55"}`}>
                  <td className="py-2 pr-3 font-mono text-slate-300">{rr.tournament_id}</td>
                  <td className="py-2 pr-3"><span className="px-1.5 py-0.5 rounded bg-[#140f02] border border-[#E5B842]/30 text-[#E5B842] font-mono text-[10px]">{tierLabel(rr.tier_code)}</span></td>
                  <td className="py-2 pr-3 text-slate-200">{rr.placement_label || "—"}</td>
                  <td className="py-2 pr-3 text-right font-heading font-black text-[#E5B842] tabular-nums">{rr.points_awarded}</td>
                  <td className="py-2">
                    {rr.counts_in_best8
                      ? <span className="inline-flex items-center gap-1 text-emerald-300 font-mono text-[10px]">Counts · #{rr.best8_rank}</span>
                      : <span className="text-slate-600 font-mono text-[10px]">Outside best-8</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[10px] font-mono text-slate-600">Tier points equal the raw WSTF event points (e.g. WSTF 3000 Champion = 3000). Only the best 8 feed the season total; the rest are shown for transparency.</p>
        </div>
      )}
    </div>
  );
}
